import { makeStyles } from "tss-react/mui"
import { Link, useParams } from "react-router-dom"
import { Box, Stack, Typography, IconButton } from "@mui/material"
import { useApiQuery, usePublicApiQuery } from "../../../../core/api/useApiQuery"
import { fetchOrderDetails, fetchPublicOrderDetails, OrderDetailsResponse } from "../home/meetingServiceApi"
import { UserWeekMenu } from "../home/weekMenuApi"
import { tailwindPalette } from "../../../theme/tailwindPalette"
import { ReceiptDetailsSkeleton } from "./ReceiptDetailsSkeleton"
import { SharePrintButtons } from "./components/SharePrintButtons"
import { ChevronLeft, ChevronRight } from "@mui/icons-material"
import { useMemo, useState } from "react"
import { useAppSelector } from "../../../store/storeHooks"
import { getPublicMeetingReceiptRoute, LOGIN_ROUTE } from "../../../../constants/routes"
import { MenuMeeting } from "../home/weekMenuTypes"
import { selectEffectiveUserId } from "../../../context/contextSelectors"
import { LocalizedStrict } from "../../../localization/components/AppLocalized"
import { MeetingServiceOrder as MeetingServiceOrderAccounting } from "../../../merchant/accounting/accounting/accountingTypes"
import { MeetingServiceOrder } from "../../../merchant/merchant/production/meetingService/meetingServiceTypes"
import { StandardDialog } from "../../../../shared/components/dialogs/StandardDialog"
import FacilitynetLogo from "../../../../shared/layouts/assets/facilit-net-logo.svg"
import { MeetingDetailsOrder } from "../../../serviceCenter/report/home/meetings/meetingTypes"
import {
    fetchMeetingServiceOrdersForExport,
    MeetingServiceOrdersForExportResponse,
    MerchantMeetingServiceOrdersQuery,
} from "../../../merchant/merchant/production/meetingService/meetingServiceApi"
import { useMerchant } from "../../../merchants/merchantHooks"
import { ReceiptDetails } from "./ReceiptDetails"
import { ServiceCenterBooking } from "../../../serviceCenter/report/home/serviceCenterHomeTypes"
import { MeetingCardOrderTypes } from "../home/components/upcomingOrders/meetings/MeetingCardMenu"
import { useEnvironment } from "../../../environment/envHooks"
import { ReceiptAndSlipDialogHeader } from "../../../merchant/merchant/components/receiptAndSlip/ReceiptAndSlipDialogHeader"

const useStyles = makeStyles()((theme) => ({
    container: {
        backgroundColor: tailwindPalette.slate[100],
        minHeight: "100vh",
        justifyContent: "start",
        paddingBottom: "3.5rem",
        "@media print": {
            flex: "none",
            display: "block",
            backgroundColor: "transparent",
            margin: "0 auto",
            padding: 0,
            width: "100%",
            maxWidth: "190mm",
            minHeight: "auto",
            height: "auto",
            boxSizing: "border-box",
            overflow: "hidden",
            pageBreakInside: "auto",
            pageBreakAfter: "avoid",
        },
        [theme.breakpoints.down("sm")]: {
            backgroundColor: theme.palette.common.white,
            gap: 0,
        },
    },
    header: {
        zIndex: 1,
        display: "grid",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "1.5rem",
        backgroundColor: "white",
        position: "sticky",
        top: 0,
        boxShadow: "0px 2px 6px 0px #00000014",
        gridTemplateColumns: "minmax(0,1fr) auto minmax(0,1fr)", // L | C | R
        "@media print": {
            display: "none",
            padding: 0,
            height: 0,
        },
        [theme.breakpoints.down("sm")]: {
            gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)",
            padding: "0 1.5rem",
            zIndex: 1,
            boxShadow: "0px 3px 1px -2px #00000033,  0px 2px 2px 0px #00000024, 0px 1px 5px 0px #0000001F",
        },
    },
    carouselContainer: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: "1rem",
    },
    carouselText: {
        fontSize: "1rem",
    },
    "@media print": {
        "@page": {
            size: "A4",
            margin: "10mm", // Standard A4 margins
        },
        body: {
            margin: 0,
            padding: 0,
            width: "210mm",
            height: "297mm",
            boxSizing: "border-box",
        },
        "*": {
            display: "none",
        },
        ".slideContainer, .slideContainer *": {
            display: "block !important",
        },
    },
    noOrderContainer: {
        display: "flex",
        height: "100%",
        flexDirection: "column",
        overflow: "hidden",
    },
    noOrderTitle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexGrow: 1,
    },
    logo: {
        width: "auto",
    },
    logoLink: {
        display: "flex",
        alignItems: "center",
        [theme.breakpoints.up("sm")]: {
            display: "none",
        },
    },
    actionButtons: {
        justifySelf: "end",
    },
    navigationContainer: {
        [theme.breakpoints.down("sm")]: {
            display: "none",
        },
    },
}))

type MockServiceCenterBooking = ServiceCenterBooking & { uid?: string } // To prevent ts errors for now as api does not return uid

export type AnyMeeting =
    | MeetingServiceOrder
    | MeetingServiceOrderAccounting
    | MenuMeeting
    | MeetingDetailsOrder
    | MockServiceCenterBooking
    | MeetingCardOrderTypes

export function hasDeliveryDetails(
    order: AnyMeeting,
): order is MeetingServiceOrder | MeetingServiceOrderAccounting | MenuMeeting | MeetingCardOrderTypes {
    return "deliveryDetails" in order
}

type MinimalMeeting<T extends { id: any; meetingDetails: { meetingName: any }; uid?: any }> = Pick<T, "id" | "uid"> & {
    meetingDetails: Pick<T["meetingDetails"], "meetingName">
}

export type AnyMinimalMeeting = MinimalMeeting<AnyMeeting>

type StartPageLayoutProps = Readonly<{
    id?: number
    onClose?: () => void
    meetings?: AnyMeeting[]
    open?: boolean
    updateEdge?: string
    singleMode?: boolean
}>

const transformData = (data: OrderDetailsResponse) => (data?.orders.length ? data?.orders[0] : undefined)
const transformOrdersForExport = (data: MeetingServiceOrdersForExportResponse) => data?.orders

const getIndexOfPreviewedOrder = (orders?: AnyMinimalMeeting[], id?: number) => {
    if (!id || !orders?.length) return 0
    return orders.findIndex((o) => o.id == id)
}

const supportEmail = "support@facilitynet.dk"

export function UserMeetingsReceipt({
    id,
    meetings = [],
    updateEdge,
    singleMode = true,
    open = false,
    onClose = () => {},
}: StartPageLayoutProps) {
    const { classes } = useStyles()
    const { orderUid: uid } = useParams()
    const { currentEnv } = useEnvironment()
    const userId = useAppSelector(selectEffectiveUserId) || 0
    const isAuthenticated = Boolean(userId)
    const enabledOrdersForExport = isAuthenticated && Boolean(meetings?.length) && !singleMode
    const merchant = enabledOrdersForExport ? useMerchant() : { merchantId: 0 }
    const isPublic = Boolean(uid)

    const [currentMeetingIndex, setCurrentMeetingIndex] = useState<number>(getIndexOfPreviewedOrder(meetings, id))
    const activeOrder = useMemo(() => meetings?.[currentMeetingIndex], [meetings, currentMeetingIndex])
    const meetingIds = useMemo(() => meetings.map((order) => Number(order.id)), [meetings])
    const hasMeetingList = meetings.length > 0
    const activeOrderId = hasMeetingList ? Number(activeOrder?.id) || 0 : (id ?? 0)

    const { data: exportOrders = [], isFetching: isFetchingOrdersForExport } = useApiQuery(
        fetchMeetingServiceOrdersForExport,
        {
            queryName: MerchantMeetingServiceOrdersQuery.ORDERS_FOR_EXPORT,
            dependencies: {
                merchantId: merchant?.merchantId ?? 0,
                orderIds: meetingIds,
            },
            enabled: enabledOrdersForExport,
            select: transformOrdersForExport,
        },
    )

    const { data: orderDetailsPublic, isLoading: orderDetailsPublicLoading } = usePublicApiQuery(
        fetchPublicOrderDetails,
        {
            queryName: UserWeekMenu.USER_MEETING_SERVICE_DETAILS_PUBLIC,
            dependencies: {
                orderUid: uid ?? "",
            },
            enabled: Boolean(uid) && isPublic,
            select: transformData,
        },
    )

    const { data: orderDetailsAuth, isLoading: orderDetailsAuthLoading } = useApiQuery(fetchOrderDetails, {
        queryName: UserWeekMenu.USER_MEETING_SERVICE_DETAILS,
        dependencies: {
            orderId: activeOrderId,
        },
        enabled: Boolean(activeOrderId) && !isPublic,
        select: transformData,
    })

    const orderDetails = orderDetailsAuth ?? orderDetailsPublic
    const orderDetailsLoading = isPublic
        ? orderDetailsPublicLoading
        : orderDetailsAuthLoading || isFetchingOrdersForExport

    const skeletonCount = !singleMode ? meetings.length || 1 : 1

    const receiptContent = useMemo(() => {
        if (orderDetailsLoading) {
            return (
                <Stack gap="4rem">
                    {Array.from({ length: skeletonCount }).map((_, index) => (
                        <ReceiptDetailsSkeleton key={index} />
                    ))}
                </Stack>
            )
        }

        if (!orderDetails) {
            return (
                <Box className={classes.noOrderContainer}>
                    <Box className={classes.noOrderTitle}>
                        {isPublic ? (
                            <LocalizedStrict
                                id="user-order-receipt-page-not-found-public-message"
                                vars={{ supportEmail }}
                                elems={{ link: <Link to={`mailto:${supportEmail}`} /> }}
                            >
                                <Typography variant="body2" fontWeight={500}>
                                    Sorry, the order wasn&apos;t found. Please contact support at{" "}
                                    <link>{supportEmail}</link> if you need help.
                                </Typography>
                            </LocalizedStrict>
                        ) : (
                            <LocalizedStrict
                                id="user-order-receipt-page-not-loaded-message"
                                vars={{ orderId: activeOrder?.id?.toString() }}
                            >
                                <Typography variant="body2" fontWeight={500}>
                                    Order id {activeOrder?.id?.toString()} could not be read. Please contact support.
                                </Typography>
                            </LocalizedStrict>
                        )}
                    </Box>
                </Box>
            )
        }

        if (singleMode) {
            return <ReceiptDetails isFirst orderDetails={orderDetails} />
        }

        return (
            <Stack gap="4rem">
                {exportOrders.map((order, index) => (
                    <ReceiptDetails key={order.id} isFirst={!index} orderDetails={order} />
                ))}
            </Stack>
        )
    }, [orderDetailsLoading, skeletonCount, orderDetails, isPublic, activeOrder?.id, singleMode, exportOrders, classes.noOrderContainer, classes.noOrderTitle])

    const handleMeetingChange = (event: any) => {
        const selectedId = event.target.value
        const index = getIndexOfPreviewedOrder(meetings, selectedId)
        setCurrentMeetingIndex(index)
    }

    const goToPreviousMeeting = () => {
        if (currentMeetingIndex > 0) {
            const prevIndex = currentMeetingIndex - 1
            setCurrentMeetingIndex(prevIndex)
        }
    }

    const goToNextMeeting = () => {
        if (currentMeetingIndex < meetings.length - 1) {
            const nextIndex = currentMeetingIndex + 1
            setCurrentMeetingIndex(nextIndex)
        }
    }

    const content = (
        <Stack direction="column" spacing="2rem" className={classes.container}>
            <Box className={classes.header}>
                <ReceiptAndSlipDialogHeader
                    handleOrderChange={handleMeetingChange}
                    id={activeOrderId}
                    uid={uid}
                    isAuthenticated={isAuthenticated}
                    isSelectMode={!singleMode}
                    orders={meetings}
                    orderDetails={orderDetails}
                />
                <Link className={classes.logoLink} to={LOGIN_ROUTE}>
                    <img className={classes.logo} src={FacilitynetLogo} alt="Facilitynet" />
                </Link>

                <Box className={classes.navigationContainer}>
                    {isAuthenticated && singleMode && hasMeetingList && (
                        <Box className={classes.carouselContainer}>
                            <IconButton onClick={goToPreviousMeeting} disabled={currentMeetingIndex === 0}>
                                <ChevronLeft />
                            </IconButton>
                            <Typography className={classes.carouselText}>
                                {currentMeetingIndex + 1}/{meetings.length}
                            </Typography>
                            <IconButton
                                onClick={goToNextMeeting}
                                disabled={currentMeetingIndex === meetings.length - 1}
                            >
                                <ChevronRight />
                            </IconButton>
                        </Box>
                    )}
                </Box>

                <Box className={classes.actionButtons}>
                    <SharePrintButtons
                        onClose={onClose}
                        hasClose={!isPublic}
                        hasShare={singleMode && !!orderDetails?.uid}
                        copyUrl={`${currentEnv?.webapp?.baseUrl}${getPublicMeetingReceiptRoute(orderDetails?.uid)}`}
                    />
                </Box>
            </Box>

            {receiptContent}
        </Stack>
    )

    return isPublic ? (
        content
    ) : (
        <StandardDialog updateEdge={updateEdge} open={open} onClose={onClose} isFullScreen hideCloseIcon topbar={<></>}>
            {content}
        </StandardDialog>
    )
}
